import { useCallback, useEffect, useRef } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/api/instance";
import type { PresentatorItem } from "../components/ProfileView/types";
import { useAuth } from "@/shared/lib/auth-context";
import { extractErrorMessage } from "../utils";
import type { Profile, ProfileMeta } from "../types";
import {
  getProfileMeta,
  patchProfileMeta,
  setProfileMeta,
  useProfileMetaById,
} from "@/entities/profile";
import { qk } from "@/shared/api/queryKeys";
import { beginTourProcessingHold, clearTourProcessingHold } from "@/features/demo-tour";

type ProfilesListResult = { profiles: Profile[] };

type PresentationItemArgs = {
  profileId: string;
  username: string;
  telephoneE164: string;
  key?: string | null;
  item: PresentatorItem;
};

export function useProfileForms() {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const profileMetaById = useProfileMetaById();
  const metaRef = useRef(profileMetaById);
  useEffect(() => {
    metaRef.current = profileMetaById;
  }, [profileMetaById]);

  const setMeta = useCallback(
    (profileId: string, patch: Partial<ProfileMeta>) => {
      patchProfileMeta(queryClient, profileId, patch);
    },
    [queryClient],
  );

  const { mutateAsync: createFormAsync } = useMutation({
    mutationFn: async ({
      profileId,
      phone,
      demoTour,
    }: {
      profileId: string;
      phone: string;
      demoTour?: boolean;
    }) => {
      const res = await api.v1.createForm(profileId, {
        phone,
        ...(demoTour ? { demo_tour: true } : {}),
      });
      return { profileId, taskId: res.data.task_id };
    },
    onSuccess: ({ profileId, taskId }) => {
      if (!taskId) {
        setMeta(profileId, {
          status: "error",
          isCreating: false,
          errorMessage:
            "Не удалось получить идентификатор задачи формирования анкеты.",
        });
        return;
      }
      setMeta(profileId, {
        status: "processing",
        taskId,
        isCreating: false,
      });
    },
    onError: (e, { profileId }) => {
      setMeta(profileId, {
        status: "error",
        isCreating: false,
        errorMessage: extractErrorMessage(
          e,
          "Не удалось сформировать анкету профиля. Повторите попытку.",
        ),
      });
    },
  });

  const { mutateAsync: upsertPresentationItemAsync } = useMutation({
    mutationFn: async (args: PresentationItemArgs) => {
      const { profileId, telephoneE164, key, item } = args;
      const res = key
        ? await api.v1.updatePresentationItem(
            profileId,
            key,
            { phone: telephoneE164 },
            { item: { ...item } },
          )
        : await api.v1.createPresentationItem(
            profileId,
            { phone: telephoneE164 },
            { item: { ...item } },
          );
      return { ...args, newKey: res.data?.key || key };
    },
    onSuccess: ({ profileId, telephoneE164, key, item, newKey }) => {
      const meta = getProfileMeta(queryClient, profileId);
      if (!meta || !meta.profileData) return;

      const updatedForms = meta.profileData.forms.map((form) => {
        if (form.telephone !== telephoneE164) return form;

        const isComment =
          form.comment?.key === key || (!key && item.name === "Комментарий");

        if (isComment) {
          return {
            ...form,
            comment: {
              key: newKey || form.comment?.key || "",
              item: { ...item },
            },
          };
        }

        const existingItemIndex = form.items.findIndex((i) => i.key === key);
        if (existingItemIndex >= 0) {
          const nextItems = [...form.items];
          nextItems[existingItemIndex] = {
            key: newKey || key || "",
            item: { ...item },
          };
          return { ...form, items: nextItems };
        }

        return {
          ...form,
          items: [
            ...form.items,
            {
              key: newKey || "",
              item: { ...item },
            },
          ],
        };
      });

      setProfileMeta(queryClient, profileId, {
        ...meta,
        profileData: {
          ...meta.profileData,
          forms: updatedForms,
        },
      });
    },
    // No onError: global MutationCache toast surfaces the failure.
  });

  const startCreateForProfile = useCallback(
    async (
      profileId: string,
      telephone: string,
      options?: { demoTour?: boolean },
    ) => {
      const tel = telephone.trim();
      if (!profileId || !tel) return;

      const username = auth.user?.username;
      if (!username) return;

      const current = metaRef.current[profileId];
      if (current?.status === "processing" || current?.isCreating) return;

      setMeta(profileId, {
        status: "processing",
        isCreating: true,
        errorMessage: null,
        telephone: tel,
        isInDatabase: true,
        isTrackingStatus: true,
      });

      if (options?.demoTour) {
        const list =
          queryClient.getQueryData<ProfilesListResult>(qk.profiles.list())
            ?.profiles ?? [];
        const displayTitle =
          list.find((p) => p.id === profileId)?.title ?? profileId;
        beginTourProcessingHold(profileId, displayTitle);
      }

      try {
        await createFormAsync({
          profileId,
          phone: tel,
          demoTour: options?.demoTour,
        });
      } catch {
        if (options?.demoTour) clearTourProcessingHold(profileId);
        // Error already applied in mutation onError (inline meta status).
      }
    },
    [auth.user?.username, createFormAsync, queryClient, setMeta],
  );

  const updatePresentatorFormItem = useCallback(
    async (args: PresentationItemArgs) => {
      const { profileId, username, telephoneE164 } = args;
      if (!profileId || !username || !telephoneE164) return;
      await upsertPresentationItemAsync(args);
    },
    [upsertPresentationItemAsync],
  );

  return { startCreateForProfile, updatePresentatorFormItem, setMeta };
}
